import { type FC, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isAxiosError } from 'axios';
import { Loader2, Plus, Trash2 } from 'lucide-react';

import { toaster, useAppForm } from '@/shared/libs';
import {
  Button,
  Dialog,
  DialogFooter,
  FormField,
  FormInput,
  FormTagsInput,
  Label,
} from '@/shared/ui';
import { FieldError } from '@/shared/ui/form';
import {
  countriesInRegions,
  REGION_META,
  REGION_PRIORITY,
} from '@entities/Offer';
import {
  buildProviderUpdatePayload,
  toCountryTagOptions,
  toProviderTagOptions,
  useCreateProviderMutation,
  useProviderOptions,
  useUpdateProviderMutation,
} from '@entities/Provider';
import type { Provider } from '@psp-aggregator/sdk';

import {
  createProviderFormSchema,
  formValuesToProviderInput,
  PROVIDER_FORM_DEFAULT_VALUES,
  type ProviderFormValues,
  providerToFormValues,
} from '../libs/providerSchema';

import { ProviderFileUpload } from './ProviderFileUpload';
import { ProviderFormGroup } from './ProviderFormGroup';

export interface ProviderFormModalProps {
  onClose: () => void;
  provider?: Provider;
}

const SectionTitle: FC<{ children: string }> = ({ children }) => (
  <p className="pt-1 text-[11px] font-semibold tracking-wider text-muted-foreground uppercase">
    {children}
  </p>
);

SectionTitle.displayName = 'ProviderFormSectionTitle';

export const ProviderFormModal: FC<ProviderFormModalProps> = ({
  onClose,
  provider,
}) => {
  const { t } = useTranslation();
  const isEdit = Boolean(provider);
  const createProvider = useCreateProviderMutation();
  const updateProvider = useUpdateProviderMutation();
  const providerOptionsQuery = useProviderOptions();
  const schema = useMemo(() => createProviderFormSchema(t), [t]);
  const form = useAppForm(schema, {
    defaultValues: provider
      ? providerToFormValues(provider)
      : PROVIDER_FORM_DEFAULT_VALUES,
  });
  const values = form.watch();
  const [coverageOpen, setCoverageOpen] = useState(true);
  const [complianceOpen, setComplianceOpen] = useState(false);
  const [contactsOpen, setContactsOpen] = useState(false);
  const [galleryOpen, setGalleryOpen] = useState(false);
  const providerStep = !values.real_name.trim()
    ? 'pages.admin.providers.steps.name'
    : !values.alias?.trim()
      ? 'pages.admin.providers.steps.alias'
      : values.countries.length === 0
        ? 'pages.admin.providers.steps.coverage'
        : undefined;
  const providerProgress = Math.round(
    ([
      Boolean(values.real_name.trim()),
      Boolean(values.alias?.trim()),
      values.countries.length > 0,
    ].filter(Boolean).length /
      3) *
      100,
  );

  const [regionFilter, setRegionFilter] = useState<string[]>([]);
  const countryOptions = useMemo(() => {
    const countries = providerOptionsQuery.data?.countries ?? [];

    if (regionFilter.length === 0 || regionFilter.includes('GLOBAL')) {
      return toCountryTagOptions(countries);
    }

    const allowed = new Set(countriesInRegions(regionFilter));

    return toCountryTagOptions(
      countries.filter((country) => allowed.has(country.code.toUpperCase())),
    );
  }, [providerOptionsQuery.data?.countries, regionFilter]);
  const currencyOptions = useMemo(
    () => toProviderTagOptions(providerOptionsQuery.data?.currencies),
    [providerOptionsQuery.data?.currencies],
  );
  const integrationOptions = useMemo(
    () => toProviderTagOptions(providerOptionsQuery.data?.integration_methods),
    [providerOptionsQuery.data?.integration_methods],
  );
  const paymentOptions = useMemo(
    () => toProviderTagOptions(providerOptionsQuery.data?.payment_methods),
    [providerOptionsQuery.data?.payment_methods],
  );
  const industryOptions = useMemo(
    () => toProviderTagOptions(providerOptionsQuery.data?.industries),
    [providerOptionsQuery.data?.industries],
  );

  const getRejectMessage = useCallback(
    (value: string): string =>
      t('pages.admin.providers.form.optionRejected', { value }),
    [t],
  );

  const submitting = createProvider.isPending || updateProvider.isPending;
  const optionsDisabled =
    providerOptionsQuery.isLoading || providerOptionsQuery.isError;

  const handleSubmit = form.handleSubmit(async (values: ProviderFormValues) => {
    const payload = formValuesToProviderInput(values);

    try {
      if (isEdit && provider) {
        await updateProvider.mutateAsync({
          payload: buildProviderUpdatePayload(provider, payload),
          providerId: provider.id,
        });
        toaster.success({ title: t('toast.providerUpdated') });
      } else {
        await createProvider.mutateAsync(payload);
        toaster.success({ title: t('toast.providerCreated') });
      }

      onClose();
    } catch (error) {
      toaster.error({
        title: isAxiosError(error)
          ? (error.response?.data?.message ??
            t(
              isEdit
                ? 'errors.providers.updateFailed'
                : 'errors.providers.createFailed',
            ))
          : t('errors.unknown'),
      });
    }
  });

  return (
    <Dialog
      title={t(
        isEdit
          ? 'pages.admin.providers.form.editTitle'
          : 'pages.admin.providers.form.createTitle',
      )}
      progress={providerProgress}
      width={720}
      onClose={onClose}>
      <form className="flex min-h-0 flex-1 flex-col" onSubmit={handleSubmit}>
        <div className="max-h-[min(60dvh,calc(90dvh-10rem))] space-y-4 overflow-y-auto overscroll-contain p-4 sm:p-6">
          <SectionTitle>
            {t('pages.admin.providers.sections.identity')}
          </SectionTitle>
          <FormField
            control={form.control}
            name="real_name"
            render={({ error, field }) => (
              <FormInput
                {...field}
                className="border-border/60 bg-background/60 focus-visible:ring-indigo-500/40 focus-visible:ring-offset-0"
                error={error}
                label={
                  <>
                    {t('pages.admin.providers.fields.realName')}
                    <span className="ml-0.5 text-rose-500">*</span>
                  </>
                }
                placeholder={t(
                  'pages.admin.providers.fields.realNamePlaceholder',
                )}
              />
            )}
          />
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <FormField
              control={form.control}
              name="alias"
              render={({ error, field }) => (
                <FormInput
                  {...field}
                  className="border-border/60 bg-background/60 focus-visible:ring-indigo-500/40 focus-visible:ring-offset-0"
                  error={error}
                  label={
                    <>
                      {t('pages.admin.providers.fields.alias')}
                      <span className="ml-0.5 text-rose-500">*</span>
                    </>
                  }
                  placeholder={t(
                    'pages.admin.providers.fields.aliasPlaceholder',
                  )}
                />
              )}
            />
            <div className="flex flex-col gap-1.5">
              <Label>{t('pages.admin.providers.fields.logo')}</Label>
              <ProviderFileUpload
                accept="image/png,image/jpeg,image/svg+xml,image/webp"
                label={t('pages.admin.providers.fields.uploadLogo')}
                preview="image"
                purpose="provider_logo"
                value={values.logo_url}
                onClear={() => form.setValue('logo_url', '')}
                onUploaded={(urls) => {
                  const url = urls[0];

                  if (url) {
                    form.setValue('logo_url', url);
                  }
                }}
              />
            </div>
          </div>
          <FormField
            control={form.control}
            name="short_description"
            render={({ error, field }) => (
              <FormInput
                {...field}
                error={error}
                label={t('pages.admin.providers.fields.shortDescription')}
                placeholder={t(
                  'pages.admin.providers.fields.shortDescriptionPlaceholder',
                )}
              />
            )}
          />
          <FormField
            control={form.control}
            name="full_description"
            render={({ error, field }) => (
              <div className="flex w-full flex-col gap-1.5">
                <Label>
                  {t('pages.admin.providers.fields.fullDescription')}
                </Label>
                <textarea
                  {...field}
                  className="min-h-28 w-full resize-none rounded-xl border border-border bg-background/60 px-3 py-2 text-sm"
                  maxLength={5000}
                  placeholder={t(
                    'pages.admin.providers.fields.fullDescriptionPlaceholder',
                  )}
                />
                <FieldError error={error} />
              </div>
            )}
          />

          <ProviderFormGroup
            open={coverageOpen}
            summary={[
              values.countries.join(', '),
              values.currencies.join(', '),
              values.integration_methods.join(', '),
              values.payment_methods.join(', '),
              values.industries.join(', '),
            ]
              .filter(Boolean)
              .join(' · ')}
            title={t('pages.admin.providers.sections.coverage')}
            onToggle={() => setCoverageOpen((open) => !open)}>
            {providerOptionsQuery.isError && (
              <p className="text-xs text-destructive">
                {t('errors.providers.optionsFailed')}
              </p>
            )}
            <FormTagsInput
              disabled={optionsDisabled}
              label={t('pages.admin.providers.fields.regionFilter')}
              options={REGION_PRIORITY.map((code) => ({
                label: REGION_META[code].name,
                value: code,
              }))}
              placeholder={t(
                'pages.admin.providers.fields.regionFilterPlaceholder',
              )}
              restrict
              value={regionFilter}
              onChange={setRegionFilter}
            />
            <p className="text-[11px] text-muted-foreground">
              {t('pages.admin.providers.fields.regionFilterHint')}
            </p>
            <FormField
              control={form.control}
              name="countries"
              render={({ error, field }) => (
                <FormTagsInput
                  disabled={optionsDisabled}
                  error={error}
                  getRejectMessage={getRejectMessage}
                  label={t('pages.admin.providers.fields.countries')}
                  options={countryOptions}
                  placeholder={t(
                    'pages.admin.providers.fields.countriesPlaceholder',
                  )}
                  restrict
                  value={field.value}
                  onChange={field.onChange}
                />
              )}
            />
            <FormField
              control={form.control}
              name="currencies"
              render={({ error, field }) => (
                <FormTagsInput
                  disabled={optionsDisabled}
                  error={error}
                  getRejectMessage={getRejectMessage}
                  label={t('pages.admin.providers.fields.currencies')}
                  options={currencyOptions}
                  placeholder={t(
                    'pages.admin.providers.fields.currenciesPlaceholder',
                  )}
                  restrict
                  value={field.value}
                  onChange={field.onChange}
                />
              )}
            />
            <FormField
              control={form.control}
              name="integration_methods"
              render={({ error, field }) => (
                <FormTagsInput
                  disabled={optionsDisabled}
                  error={error}
                  getRejectMessage={getRejectMessage}
                  label={t('pages.admin.providers.fields.integrationMethods')}
                  options={integrationOptions}
                  placeholder={t(
                    'pages.admin.providers.fields.integrationMethodsPlaceholder',
                  )}
                  restrict
                  value={field.value}
                  onChange={field.onChange}
                />
              )}
            />
            <FormField
              control={form.control}
              name="payment_methods"
              render={({ error, field }) => (
                <FormTagsInput
                  disabled={optionsDisabled}
                  error={error}
                  getRejectMessage={getRejectMessage}
                  label={t('pages.admin.providers.fields.paymentMethods')}
                  options={paymentOptions}
                  placeholder={t(
                    'pages.admin.providers.fields.paymentMethodsPlaceholder',
                  )}
                  restrict
                  value={field.value}
                  onChange={field.onChange}
                />
              )}
            />
            <FormField
              control={form.control}
              name="industries"
              render={({ error, field }) => (
                <FormTagsInput
                  disabled={optionsDisabled}
                  error={error}
                  getRejectMessage={getRejectMessage}
                  label={t('pages.admin.providers.fields.industries')}
                  options={industryOptions}
                  placeholder={t(
                    'pages.admin.providers.fields.industriesPlaceholder',
                  )}
                  restrict
                  value={field.value}
                  onChange={field.onChange}
                />
              )}
            />
          </ProviderFormGroup>

          <ProviderFormGroup
            open={complianceOpen}
            summary={[
              values.jurisdictions.join(', '),
              values.regulators.join(', '),
            ]
              .filter(Boolean)
              .join(' · ')}
            title={t('pages.admin.providers.sections.compliance')}
            onToggle={() => setComplianceOpen((open) => !open)}>
            <FormField
              control={form.control}
              name="jurisdictions"
              render={({ error, field }) => (
                <FormTagsInput
                  error={error}
                  label={t('pages.admin.providers.fields.jurisdictions')}
                  placeholder={t(
                    'pages.admin.providers.fields.jurisdictionsPlaceholder',
                  )}
                  value={field.value}
                  onChange={field.onChange}
                />
              )}
            />
            <FormField
              control={form.control}
              name="regulators"
              render={({ error, field }) => (
                <FormTagsInput
                  error={error}
                  label={t('pages.admin.providers.fields.regulators')}
                  placeholder={t(
                    'pages.admin.providers.fields.regulatorsPlaceholder',
                  )}
                  value={field.value}
                  onChange={field.onChange}
                />
              )}
            />
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>{t('pages.admin.providers.fields.licenses')}</Label>
                <button
                  className="inline-flex cursor-pointer items-center gap-1 text-xs text-indigo-600 hover:underline dark:text-indigo-300"
                  type="button"
                  onClick={() =>
                    form.setValue('licenses', [
                      ...values.licenses,
                      { document_url: '', name: '' },
                    ])
                  }>
                  <Plus className="h-3.5 w-3.5" />
                  {t('buttons.add')}
                </button>
              </div>
              {values.licenses.map((license, index) => (
                <div
                  key={`${license.name}-${index}`}
                  className="flex items-center gap-2">
                  <input
                    className="h-10 min-w-0 flex-1 rounded-xl border border-border bg-background/60 px-3 text-sm"
                    placeholder={t(
                      'pages.admin.providers.fields.licensePlaceholder',
                    )}
                    value={license.name}
                    onChange={(event) => {
                      const next = [...values.licenses];

                      next[index] = { ...license, name: event.target.value };
                      form.setValue('licenses', next);
                    }}
                  />
                  <ProviderFileUpload
                    accept="application/pdf,image/*"
                    label={t('pages.admin.providers.fields.uploadDocument')}
                    purpose="provider_document"
                    value={license.document_url}
                    onUploaded={(urls) => {
                      const url = urls[0];

                      if (!url) {
                        return;
                      }

                      const next = [...form.getValues('licenses')];

                      next[index] = {
                        ...next[index],
                        document_url: url,
                      };
                      form.setValue('licenses', next);
                    }}
                  />
                  <button
                    aria-label={t('buttons.delete')}
                    className="cursor-pointer rounded-lg p-1.5 text-muted-foreground hover:bg-rose-500/10 hover:text-rose-500"
                    type="button"
                    onClick={() =>
                      form.setValue(
                        'licenses',
                        values.licenses.filter(
                          (_, itemIndex) => itemIndex !== index,
                        ),
                      )
                    }>
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              ))}
            </div>
          </ProviderFormGroup>

          <ProviderFormGroup
            open={contactsOpen}
            summary={[
              values.contact_email,
              values.contact_phone,
              values.website,
            ]
              .map((item) => item?.trim() ?? '')
              .filter(Boolean)
              .join(' · ')}
            title={t('pages.admin.providers.sections.contacts')}
            onToggle={() => setContactsOpen((open) => !open)}>
            <FormField
              control={form.control}
              name="contact_name"
              render={({ error, field }) => (
                <FormInput
                  {...field}
                  error={error}
                  label={t('pages.admin.providers.fields.contactName')}
                  placeholder={t(
                    'pages.admin.providers.fields.contactNamePlaceholder',
                  )}
                />
              )}
            />
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <FormField
                control={form.control}
                name="contact_email"
                render={({ error, field }) => (
                  <FormInput
                    {...field}
                    error={error}
                    label={t('pages.admin.providers.fields.contactEmail')}
                    placeholder={t(
                      'pages.admin.providers.fields.contactEmailPlaceholder',
                    )}
                    type="email"
                  />
                )}
              />
              <FormField
                control={form.control}
                name="contact_phone"
                render={({ error, field }) => (
                  <FormInput
                    {...field}
                    error={error}
                    label={t('pages.admin.providers.fields.contactPhone')}
                    placeholder={t(
                      'pages.admin.providers.fields.contactPhonePlaceholder',
                    )}
                  />
                )}
              />
            </div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <FormField
                control={form.control}
                name="website"
                render={({ error, field }) => (
                  <FormInput
                    {...field}
                    error={error}
                    label={t('pages.admin.providers.fields.website')}
                    placeholder={t(
                      'pages.admin.providers.fields.websitePlaceholder',
                    )}
                  />
                )}
              />
              <FormField
                control={form.control}
                name="linkedin"
                render={({ error, field }) => (
                  <FormInput
                    {...field}
                    error={error}
                    label={t('pages.admin.providers.fields.linkedin')}
                    placeholder={t(
                      'pages.admin.providers.fields.linkedinPlaceholder',
                    )}
                  />
                )}
              />
            </div>
            <FormField
              control={form.control}
              name="social_links"
              render={({ error, field }) => (
                <FormTagsInput
                  error={error}
                  label={t('pages.admin.providers.fields.socialLinks')}
                  placeholder={t(
                    'pages.admin.providers.fields.socialLinksPlaceholder',
                  )}
                  value={field.value}
                  onChange={field.onChange}
                />
              )}
            />
            <FormField
              control={form.control}
              name="messengers"
              render={({ error, field }) => (
                <FormTagsInput
                  error={error}
                  label={t('pages.admin.providers.fields.messengers')}
                  placeholder={t(
                    'pages.admin.providers.fields.messengersPlaceholder',
                  )}
                  value={field.value}
                  onChange={field.onChange}
                />
              )}
            />
          </ProviderFormGroup>
          <ProviderFormGroup
            open={galleryOpen}
            summary={
              values.gallery_urls.length > 0
                ? String(values.gallery_urls.length)
                : ''
            }
            title={t('pages.admin.providers.sections.gallery')}
            onToggle={() => setGalleryOpen((open) => !open)}>
            {values.gallery_urls.length < 20 && (
              <ProviderFileUpload
                accept="image/*,video/*"
                label={t('pages.admin.providers.fields.addGallery')}
                purpose="provider_gallery"
                onUploaded={(urls) =>
                  form.setValue(
                    'gallery_urls',
                    [...values.gallery_urls, ...urls].slice(0, 20),
                  )
                }
              />
            )}
            {values.gallery_urls.length > 0 && (
              <div className="grid grid-cols-4 gap-2">
                {values.gallery_urls.map((url, index) => (
                  <div
                    key={`${url}-${index}`}
                    className="group relative aspect-square overflow-hidden rounded-lg border border-border/60 bg-muted">
                    <img
                      alt=""
                      className="h-full w-full object-cover"
                      src={url}
                    />
                    <button
                      aria-label={t('buttons.delete')}
                      className="absolute top-1 right-1 cursor-pointer rounded-md bg-black/50 p-1 text-white opacity-0 transition-opacity group-hover:opacity-100"
                      type="button"
                      onClick={() =>
                        form.setValue(
                          'gallery_urls',
                          values.gallery_urls.filter(
                            (_, itemIndex) => itemIndex !== index,
                          ),
                        )
                      }>
                      <Trash2 className="h-3 w-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </ProviderFormGroup>
        </div>

        <DialogFooter
          className="shrink-0 flex-row items-center justify-between gap-2 border-t border-border/40 bg-card px-4 py-3 sm:flex-row sm:px-6"
          endSlot={
            <Button
              className={
                providerStep
                  ? 'h-10 min-w-0 flex-1 bg-foreground/10 text-muted-foreground shadow-none hover:bg-foreground/10 disabled:opacity-100'
                  : 'h-10 min-w-0 flex-1 bg-gradient-to-r from-indigo-500 to-violet-600 text-white shadow-lg shadow-indigo-500/25 hover:opacity-95'
              }
              disabled={Boolean(providerStep) || submitting}
              type="submit">
              {submitting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : providerStep ? (
                t(providerStep)
              ) : isEdit ? (
                t('pages.admin.providers.form.saveAction')
              ) : (
                t('pages.admin.providers.form.createAction')
              )}
            </Button>
          }
          startSlot={
            <button
              className="h-10 shrink-0 cursor-pointer rounded-xl px-3 text-sm font-medium text-muted-foreground hover:bg-foreground/5 hover:text-foreground"
              type="button"
              onClick={onClose}>
              {t('buttons.cancel')}
            </button>
          }
        />
      </form>
    </Dialog>
  );
};

ProviderFormModal.displayName = 'ProviderFormModal';
